import cx from 'classnames';
import type { FC, ReactNode } from 'react';
import { useId, useState } from 'react';

import type { IconName } from '@zen/Styleguide';
import { createTrackingParentAttribute } from '@zen/utils/tracking';

import { IconButton } from '../Button';
import CollapsibleElement from '../CollapsibleElement';
import Icon from '../Icon';
import { Headline } from '../Typography';
import { getToggleIcon, getToggleLabel } from './helpers';

interface Props {
  actions?: ReactNode;
  children?: ReactNode;
  className?: string;
  compactView?: boolean;
  control?: ReactNode;
  footer?: ReactNode;
  headerContent?: ReactNode;
  id?: string;
  isCollapsedByDefault?: boolean;
  isCollapsible?: boolean;
  subtitle?: string;
  title?: string;
  titleIcon?: IconName;
}

const Container: FC<Props> = (props) => {
  const {
    actions,
    children,
    className = '',
    compactView = false,
    control,
    footer,
    headerContent,
    isCollapsedByDefault = false,
    isCollapsible = false,
    subtitle,
    title,
    titleIcon,
    ...rest
  } = props;
  const [isExpanded, setIsExpanded] = useState<boolean>(!isCollapsedByDefault);
  const bodyId: string = useId();

  const isContentVisible: boolean = !isCollapsible || isExpanded;
  const hasVisibleChildren: boolean = !!children && isContentVisible;
  const isHeadlineVisible: boolean = !!title || !!subtitle || !!actions || isCollapsible;
  const toggleIcon: IconName = getToggleIcon(isExpanded);
  const toggleLabel: string = getToggleLabel(isExpanded);

  const headerClassNames: string = cx({ 'border-b border-solid border-grey-lighter': hasVisibleChildren }, 'px-6 pt-4');
  const childrenClassName: string = cx({ 'p-6': !compactView });
  const containerClassNames: string = cx('bg-white border border-solid border-grey-lighter rounded-sm', className);

  const handleToggle = (): void => setIsExpanded(!isExpanded);

  const body: ReactNode = (
    <>
      {children && <div className={childrenClassName}>{children}</div>}
      {footer && <div className="py-4 pl-6">{footer}</div>}
    </>
  );

  const renderBody = (): ReactNode => {
    if (!isCollapsible) {
      return body;
    }

    return (
      <div id={bodyId}>
        <CollapsibleElement isOpened={isExpanded}>{body}</CollapsibleElement>
      </div>
    );
  };

  return (
    <div className={containerClassNames} {...createTrackingParentAttribute('container')} data-testid="container" {...rest}>
      <div className={headerClassNames} data-testid="container-header">
        {isHeadlineVisible && (
          <div className="flex justify-between items-center pb-4">
            <div className="flex items-center gap-6">
              {isCollapsible && (
                <IconButton
                  aria-controls={bodyId}
                  aria-expanded={isExpanded}
                  icon={toggleIcon}
                  onClick={handleToggle}
                  size="medium"
                  title={toggleLabel}
                  variant="ghost"
                />
              )}
              <div>
                <Headline level={3}>
                  <>
                    {titleIcon && <Icon className="mr-2" icon={titleIcon} />}
                    {title}
                  </>
                </Headline>
                {subtitle && <p className="mt-1 text-sm leading-6 text-grey-base">{subtitle}</p>}
              </div>
            </div>
            {actions && <div className="flex space-x-4">{actions}</div>}
          </div>
        )}
        {headerContent && <div>{headerContent}</div>}
        {control}
      </div>
      {renderBody()}
    </div>
  );
};

export type { Props as ContainerProps };
export default Container;
